import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { ScrollArea } from '@mantine/core';
import cn from 'classnames';

import THead from './THead/THead';
import TBody from './TBody/TBody';
import Pagination from './Pagination';
import { AggregateDisplay } from './AggregateDisplay';
import { useTableInstance, useSkipper, useColumns } from './hooks';
import { reconcileColumnOrder, isSameOrder } from './hooks/reconcileColumnOrder';
import { useRowPosition } from './hooks/useRowPosition';
import { RTableProps } from './RTable.props';
import { DragDropContainer } from './components';
import {
  useReactTableController,
  useReactTableControllerInstance,
} from './hooks/useReactTableController';
import type {
  Updater,
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
  PaginationState,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';

import styles from './RTable.module.css';

const DEFAULT_PAGE_SIZE = 10;

/**
 * RTable — таблица с внешним стором (ReactTableController).
 */
export default function RTable<T>(props: RTableProps<T>) {
  const {
    ref,
    onCenteredRowChange,
    id: tableId,
    data = [],
    columns = [],
    className = '',
    withColumnBorders = true,

    // Initial state props (используются только при первом создании контроллера)
    initialColumnVisibility = {},
    initialColumnOrder = columns.map(c => c.id!),
    initialSorting = [],

    // Pagination props
    pagination,
    onPaginationChange,

    // Row and cell customization props
    getRowProps = () => ({}),
    getCellProps = () => ({}),
    getRowId = (_, relativeIndex, parent) =>
      parent ? [parent.id, relativeIndex].join('.') : relativeIndex.toString(),

    // Feature flags
    enableRowSelection = false,
    enableColumnFilters = false,

    // Row selection props
    rowSelection: externalRowSelection,
    onRowSelectionChange = () => ({}),

    // Expandable rows props.
    // Дефолтов здесь быть не должно: пустой renderSubComponent рисовал бы
    // пустую полосу под каждой раскрытой строкой дерева, а `() => false`
    // у getRowCanExpand перебивал бы штатный фолбэк TanStack «раскрываемо,
    // если есть дети» (getRowCanExpand?.(row) ?? subRows.length > 0).
    renderSubComponent,
    getRowCanExpand,
    getSubRows,
    expanded,
    onExpandedChange,

    // Data update props
    rowId,
    onUpdateTableData = () => ({}),

    // Summary row
    summaryRow,

    // Click events
    onRowClick,
    onRowDoubleClick,
    onCellClick,

    // Row height
    rowHeight = 1,
    // Body height for internal scroll
    bodyHeight,

    // Callbacks
    onSortingChange,
    onColumnFiltersChange,

    // Агрегаты
    aggregateData,
    aggregateConfig,
    aggregatesLoading,

    emptyPlaceholder,
  } = props;

  const viewportRef = useRef<HTMLDivElement>(null);
  const rowPosition = useRowPosition(viewportRef);
  useImperativeHandle(ref, () => rowPosition, [rowPosition]);

  // Получаем контроллер (создаётся один раз для tableId)
  const controller = useReactTableControllerInstance(tableId);

  // Подписываемся на состояние контроллера через селекторы
  const storedColumnOrder = useReactTableController(tableId, s => s.columnOrder);
  const columnSizing = useReactTableController(tableId, s => s.columnSizing);
  const columnVisibility = useReactTableController(tableId, s => s.columnVisibility);
  const paginationState = useReactTableController(tableId, s => s.paginationState);
  const sorting = useReactTableController(tableId, s => s.sorting);
  const columnFilters = useReactTableController(tableId, s => s.columnFilters);
  const storedRowSelection = useReactTableController(tableId, s => s.rowSelection);

  // Контролируемое выделение: проп передан — владеет потребитель, стор таблицы
  // в этом режиме не используется (иначе получилось бы два набора галочек).
  const isSelectionControlled = externalRowSelection !== undefined;
  const rowSelection = isSelectionControlled ? externalRowSelection : storedRowSelection;

  // Вычисляем pagination метаданные
  const total = pagination && pagination?.total ? pagination.total : data.length;
  const pageSize =
    pagination && pagination?.pageSize
      ? pagination.pageSize
      : pagination !== false
        ? DEFAULT_PAGE_SIZE
        : data.length;

  // Инициализация контроллера (один раз при монтировании)
  useEffect(() => {
    // Устанавливаем начальные значения только если:
    // 1. Контроллер создаётся впервые (не был инициализирован)
    // 2. И нет сохранённых настроек в localStorage
    // (чтобы не перезаписать сохранённые настройки при перезагрузке страницы)
    if (!controller.isTableInitialized() && !controller.hasStoredSettings()) {
      controller.setColumnOrder(initialColumnOrder);
      controller.setColumnVisibility(initialColumnVisibility);
      controller.setSorting(initialSorting);
      controller.setPaginationState({
        pageIndex: pagination && pagination?.current ? pagination.current - 1 : 0,
        pageSize,
      });
    }
    controller.markAsInitialized();

    // Cleanup при размонтировании
    // Не удаляем контроллер, т.к. он переиспользуется между маунтами
    // return () => { ReactTableController.destroyInstance(tableId); };
  }, []);

  // Фактический набор колонок — без служебных `select`/`expander`: их
  // добавляет useTableInstance поверх порядка, в сохранённом порядке они не
  // участвуют. Ссылка стабильна по содержимому: потребители нередко передают
  // новый массив колонок на каждый рендер, а от неё зависят примирённый
  // порядок (и с ним модель колонок TanStack) и эффект записи в стор.
  const columnIdsRef = useRef<string[]>([]);
  const nextColumnIds = columns
    .map(c => c.id)
    .filter((id): id is string => typeof id === 'string' && id !== '');
  if (!isSameOrder(columnIdsRef.current, nextColumnIds)) {
    columnIdsRef.current = nextColumnIds;
  }
  const columnIds = columnIdsRef.current;

  // Сохранённый порядок примиряем с фактическим набором: настройки могли быть
  // сохранены под другой набор колонок (добавили колонку, переименовали id,
  // динамический пивот). Без примирения TanStack дописывал бы незнакомые колонки
  // в конец, а dnd-kit не давал бы их перетащить — в SortableContext уходит
  // именно этот список. Дальше везде используется примирённый порядок.
  const columnOrder = useMemo(
    () => reconcileColumnOrder(storedColumnOrder, columnIds),
    [storedColumnOrder, columnIds]
  );

  // Примирённый порядок возвращаем в стор, только если он отличается: иначе
  // каждая перерисовка писала бы в localStorage. Сравниваем с актуальным
  // снимком, а не с замыканием: эффект инициализации выше мог только что
  // записать initialColumnOrder, и его нельзя перебить порядком из кода.
  //
  // TODO: позиция скрытой колонки не переживает скрытие/показ. RepositoryTable
  // передаёт сюда только видимые колонки (getVisibleColumns фильтрует скрытые),
  // поэтому id скрытой выпадает из `columnIds` и вычищается из стора — при
  // обратном показе колонка встаёт по соседу из кода, а не туда, куда её
  // перетащил пользователь. Чинится разведением двух списков: рендер остаётся
  // перестановкой `columnIds`, а в стор пишется порядок, где неизвестные id
  // сохраняются на своих местах. Ждём, выстрелит ли на практике.
  useEffect(() => {
    const current = controller.getSnapshot().columnOrder;
    const next = reconcileColumnOrder(current, columnIds);
    if (!isSameOrder(next, current)) {
      controller.setColumnOrder(next);
    }
  }, [controller, columnIds, storedColumnOrder]);

  // Синхронизация с внешними колбэками
  useEffect(() => {
    if (onSortingChange) {
      onSortingChange(sorting);
    }
  }, [sorting, onSortingChange]);

  useEffect(() => {
    if (onColumnFiltersChange) {
      onColumnFiltersChange(columnFilters);
    }
  }, [columnFilters, onColumnFiltersChange]);

  const [, skipAutoResetPageIndex] = useSkipper();

  // Expandable rows включены если потребитель явно передал renderSubComponent
  const hasExpandableRows = props.renderSubComponent !== undefined;

  // Обновляем колонки (добавляем checkbox и/или expander если нужны)
  const { updatedColumns } = useColumns({
    columns,
    enableRowSelection,
    hasExpandableRows,
  });

  // Создаём обертки для setters контроллера, которые поддерживают updater functions
  // Важно: берем актуальное значение из контроллера, а не из замыкания
  const setColumnOrderWrapper = (updater: Updater<ColumnOrderState>) => {
    const current = controller.getSnapshot().columnOrder;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setColumnOrder(next);
  };

  const setColumnSizingWrapper = (updater: Updater<ColumnSizingState>) => {
    const current = controller.getSnapshot().columnSizing;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setColumnSizing(next);
  };

  const setColumnVisibilityWrapper = (updater: Updater<VisibilityState>) => {
    const current = controller.getSnapshot().columnVisibility;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setColumnVisibility(next);
  };

  const setPaginationStateWrapper = (updater: Updater<PaginationState>) => {
    const current = controller.getSnapshot().paginationState;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setPaginationState(next);
  };

  const setSortingWrapper = (updater: Updater<SortingState>) => {
    const current = controller.getSnapshot().sorting;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setSorting(next);
  };

  const setColumnFiltersWrapper = (updater: Updater<ColumnFiltersState>) => {
    const current = controller.getSnapshot().columnFilters;
    const next = typeof updater === 'function' ? updater(current) : updater;
    controller.setColumnFilters(next);
  };

  // Создаём TanStack Table инстанс
  const table = useTableInstance<T>({
    data,
    columns: updatedColumns,
    enableRowSelection,
    enableColumnFilters,
    rowSelection,
    onRowSelectionChange: updater => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      if (!isSelectionControlled) {
        controller.setRowSelection(next);
      }
      if (onRowSelectionChange) {
        onRowSelectionChange(next);
      }
    },
    onPaginationChange,
    getRowCanExpand,
    getSubRows,
    expanded,
    onExpandedChange,
    hasExpandableRows,
    getRowId,
    onUpdateTableData,
    skipAutoResetPageIndex,
    // State из контроллера
    columnOrder,
    columnSizing,
    columnVisibility,
    paginationState,
    sorting,
    columnFilters,
    // Setters из контроллера с поддержкой updater functions
    setColumnOrder: setColumnOrderWrapper,
    setColumnSizing: setColumnSizingWrapper,
    setColumnVisibility: setColumnVisibilityWrapper,
    setPaginationState: setPaginationStateWrapper,
    setSorting: setSortingWrapper,
    setColumnFilters: setColumnFiltersWrapper,
  });

  // Pagination UI logic
  const showTopPagination = useMemo(
    () => data.length > 0 && pagination !== false && pagination?.showTop === true,
    [data, pagination]
  );

  const showBottomPagination = useMemo(
    () => data.length > 0 && pagination !== false && pagination?.showBottom !== false,
    [data, pagination]
  );

  const currentPage =
    pagination && pagination.current ? pagination.current : paginationState.pageIndex + 1;

  const onPaginate = (current: number) => {
    controller.setPaginationState({ pageIndex: current - 1, pageSize });
    if (typeof onPaginationChange === 'function') {
      onPaginationChange({ current, total, pageSize });
    }
  };

  return (
    <DragDropContainer tableId={tableId}>
      <div className={styles.container} data-rtable-root>
        <div className={styles.wrapper}>
          {showTopPagination && (
            <div className={styles.pagination}>
              <Pagination
                current={currentPage}
                total={total}
                pageSize={pageSize}
                onChange={onPaginate}
                pageSizeOptions={pagination !== false ? pagination?.pageSizeOptions : undefined}
                onPageSizeChange={pagination !== false ? pagination?.onPageSizeChange : undefined}
              />
            </div>
          )}

          <div className={styles.scrollWrapper}>
            <ScrollArea
              h={bodyHeight || '100%'}
              viewportRef={viewportRef}
              onScrollPositionChange={
                onCenteredRowChange
                  ? () => {
                      const rowId = rowPosition.getCenteredRowId();
                      if (rowId !== null) onCenteredRowChange(rowId);
                    }
                  : undefined
              }
            >
              <div
                className={cn(styles.table, className)}
                style={{
                  minWidth: table.getTotalSize(),
                  // CSS-переменную читают TCell/SummaryRow (var с дефолтом на границу)
                  ...(withColumnBorders === true ? {} : { '--rtable-column-border': 'none' }),
                }}
                // Режим разделителей шапки читает THead.module.css:
                // all — на всю высоту, header — укороченные по центру, none — без них
                data-column-borders={
                  withColumnBorders === true
                    ? 'all'
                    : withColumnBorders === false
                      ? 'none'
                      : withColumnBorders
                }
              >
                <THead<T>
                  tableId={tableId}
                  table={table}
                  columnOrder={columnOrder}
                  enableColumnFilters={enableColumnFilters}
                  rowId={rowId}
                />
                <TBody<T>
                  tableId={tableId}
                  table={table}
                  columnOrder={columnOrder}
                  getRowProps={getRowProps}
                  getCellProps={getCellProps}
                  renderSubComponent={renderSubComponent}
                  summaryRow={summaryRow}
                  data={data}
                  onRowClick={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  onCellClick={onCellClick}
                  rowHeight={rowHeight}
                  emptyPlaceholder={emptyPlaceholder}
                />
              </div>
            </ScrollArea>
          </div>

          {showBottomPagination && (
            <div className={styles.pagination}>
              <AggregateDisplay
                total={total}
                data={aggregateData ?? undefined}
                config={aggregateConfig}
                loading={aggregatesLoading}
                selectedCount={Object.values(rowSelection).filter(Boolean).length}
              />
              <Pagination
                current={currentPage}
                total={total}
                pageSize={pageSize}
                onChange={onPaginate}
                pageSizeOptions={pagination !== false ? pagination?.pageSizeOptions : undefined}
                onPageSizeChange={pagination !== false ? pagination?.onPageSizeChange : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </DragDropContainer>
  );
}
